import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

type FAQItem = {
  question: string;
  answer: React.ReactNode;
};

const FAQS: FAQItem[] = [
  {
    question: "Is ReasonShield a doctor?",
    answer: (
      <p>
        No. ReasonShield is clinical decision support software (CDSS) that assists healthcare professionals by transforming laboratory data into structured, evidence-based insights. It supports, but does not replace, clinical judgment or decision-making by licensed providers.
      </p>
    )
  },
  {
    question: "Is ReasonShield a laboratory?",
    answer: (
      <p>
        No. ReasonShield is not a laboratory. It is a technology platform that analyzes validated results from certified clinical labs and delivers treatment guidance and safety insights based on established medical guidelines.
      </p>
    )
  },
  {
    question: "Can ReasonShield work with my lab?",
    answer: (
      <p>
        Yes. ReasonShield can integrate with most laboratory information systems (LIS) and supports standard data formats such as HL7. Our team can assist with compatibility checks and integration setup.
      </p>
    )
  },
  {
    question: "Does ReasonShield validate lab data?",
    answer: (
      <p>
        ReasonShield processes data that has already been validated by certified laboratories. While we perform internal checks to maintain data consistency and quality, the source laboratory remains responsible for the clinical accuracy of the raw results.
      </p>
    )
  },
  {
    question: "How does ReasonShield process microbiology data?",
    answer: (
      <p>
        ReasonShield uses rule-based algorithms and curated clinical databases to interpret microbiology results. It identifies pathogens, analyzes antimicrobial resistance patterns, and generates treatment recommendations aligned with recognized infectious disease guidelines and patient-specific factors.
      </p>
    )
  },
  {
    question: "Where do the treatment recommendations come from?",
    answer: (
      <div>
        <p className="mb-4">
          Treatment recommendations are derived from multiple evidence-based sources integrated into the ReasonShield platform:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Clinical Guidelines (WHAT to prescribe):</strong> Based on authoritative infectious disease references such as IDSA, Sanford Guide, and Johns Hopkins ABX Guide.</li>
          <li><strong>Dosing & Administration (HOW to prescribe):</strong> Draws on established antibiotic guidance for routes, dosages, and contraindications.</li>
          <li><strong>FDA FAERS Database:</strong> Incorporates adverse event data to generate a safety-based Adverse Score (0.1–0.9).</li>
          <li><strong>NADAC Pricing Data:</strong> Uses national average drug cost information to suggest cost-effective options.</li>
        </ul>
        <p className="mt-4 text-sm italic">
          ReasonShield is not affiliated with or endorsed by IDSA, Sanford Guide, Johns Hopkins, or the U.S. FDA.
        </p>
      </div>
    )
  },
  {
    question: "What is the Adverse Score?",
    answer: (
      <div>
        <p className="mb-4">
          The Adverse Score quantifies relative drug safety using data from the FDA's Adverse Event Reporting System (FAERS). It weights the severity and frequency of reported events:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Death (100%)</li>
          <li>Life-threatening (80%)</li>
          <li>Hospitalization (60%)</li>
          <li>Disability (70%)</li>
          <li>Other serious (40%)</li>
        </ul>
        <p className="mt-4">
          The result is a normalized score from 0.1 (low risk) to 0.9 (high risk), helping clinicians identify safer alternatives.
        </p>
      </div>
    )
  },
  {
    question: "How are treatment recommendations prioritized?",
    answer: (
      <div>
        <p className="mb-4">Each drug is ranked using five key factors:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Clinical effectiveness against the detected pathogen</li>
          <li>Safety (Adverse Score)</li>
          <li>Route of administration (oral preferred when suitable)</li>
          <li>Cost and availability</li>
          <li>Resistance compatibility and clinical context</li>
        </ul>
      </div>
    )
  },
  {
    question: "Are you affiliated with IDSA, Sanford Guide, Johns Hopkins, or the FDA?",
    answer: (
      <p>
        No. ReasonShield independently integrates publicly available medical data and guideline information. We are not affiliated with, endorsed by, or sponsored by IDSA, Sanford Guide, Johns Hopkins, or the U.S. FDA.
      </p>
    )
  },
  {
    question: "Who has access to patient data?",
    answer: (
      <p>
        Only authorized healthcare providers and the patient have access to identifiable data. ReasonShield maintains HIPAA-compliant data security standards and does not share or sell patient information.
      </p>
    )
  },
  {
    question: "How fast does ReasonShield generate results?",
    answer: (
      <p>
        Most reports are generated in 2–5 seconds after receiving validated lab data. The system is optimized for both single-sample and batch processing.
      </p>
    )
  },
  {
    question: "Does ReasonShield use artificial intelligence or machine learning?",
    answer: (
      <div>
        <p className="mb-4">
          ReasonShield does not use machine learning to make treatment decisions. All recommendations are produced through rule-based clinical logic and verified databases.
        </p>
        <p>
          AI is used only to generate natural-language explanations that help clinicians understand the reasoning behind each recommendation.
        </p>
      </div>
    )
  },
];

// Related FAQs for each question
const RELATED_FAQS: { [key: string]: string[] } = {
  "Is ReasonShield a doctor?": ["Is ReasonShield a laboratory?", "Does ReasonShield use artificial intelligence or machine learning?"],
  "Is ReasonShield a laboratory?": ["Can ReasonShield work with my lab?", "Does ReasonShield validate lab data?"],
  "Can ReasonShield work with my lab?": ["Does ReasonShield validate lab data?", "How fast does ReasonShield generate results?"],
  "Does ReasonShield validate lab data?": ["How does ReasonShield process microbiology data?", "Can ReasonShield work with my lab?"],
  "How does ReasonShield process microbiology data?": ["Where do the treatment recommendations come from?", "Does ReasonShield validate lab data?"],
  "Where do the treatment recommendations come from?": ["How are treatment recommendations prioritized?", "What is the Adverse Score?"],
  "What is the Adverse Score?": ["How are treatment recommendations prioritized?", "Where do the treatment recommendations come from?"],
  "How are treatment recommendations prioritized?": ["What is the Adverse Score?", "Where do the treatment recommendations come from?"],
  "Are you affiliated with IDSA, Sanford Guide, Johns Hopkins, or the FDA?": ["Where do the treatment recommendations come from?", "Is ReasonShield a doctor?"],
  "Who has access to patient data?": ["Is ReasonShield a doctor?", "Is ReasonShield a laboratory?"],
  "How fast does ReasonShield generate results?": ["Can ReasonShield work with my lab?", "How does ReasonShield process microbiology data?"],
  "Does ReasonShield use artificial intelligence or machine learning?": ["How does ReasonShield process microbiology data?", "Where do the treatment recommendations come from?"],
};

const FAQPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [helpfulRatings, setHelpfulRatings] = useState<{[key: string]: boolean | null}>({});
  
  const toggleFAQ = (question: string) => {
    setOpenFAQ(openFAQ === question ? null : question);
  };

  const rateFAQ = (question: string, isHelpful: boolean) => {
    setHelpfulRatings({ ...helpfulRatings, [question]: isHelpful });
  };
  
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Find answers to common questions about ReasonShield's clinical microbiology analysis platform.
          </motion.p>
        </div>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="space-y-6">
            {FAQS.map((faq, index) => (
              <motion.div 
                key={faq.question}
                className="border border-border rounded-lg overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
              >
                <button
                  className="flex justify-between items-center w-full p-6 text-left bg-background-alt hover:bg-section-light transition-colors duration-200"
                  onClick={() => toggleFAQ(faq.question)}
                >
                  <h3 className="text-lg font-medium text-text">{faq.question}</h3>
                  <span>
                    {openFAQ === faq.question ? (
                      <ChevronUpIcon className="h-5 w-5 text-primary" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-primary" />
                    )}
                  </span>
                </button>
                
                {openFAQ === faq.question && (
                  <div className="p-6 bg-white border-t border-border">
                    <div className="prose prose-lg text-text max-w-none">
                      {faq.answer}
                    </div>
                    
                    {/* Related FAQs */}
                    {RELATED_FAQS[faq.question] && RELATED_FAQS[faq.question].length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-secondary uppercase tracking-wider mb-3">You may also want to read:</h4>
                        <ul className="space-y-2">
                          {RELATED_FAQS[faq.question].map((relatedQuestion) => (
                            <li key={relatedQuestion}>
                              <button 
                                className="text-primary hover:text-accent text-left"
                                onClick={() => toggleFAQ(relatedQuestion)}
                              >
                                {relatedQuestion}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Helpful rating */}
                    <div className="mt-6 pt-4 border-t border-border">
                      <p className="text-sm text-text mb-2">Was this answer helpful?</p>
                      <div className="flex space-x-2">
                        <button 
                          className={`px-4 py-1 text-sm rounded-md border ${
                            helpfulRatings[faq.question] === true 
                              ? 'bg-primary text-text-light border-primary' 
                              : 'bg-white text-text border-border hover:border-primary'
                          }`}
                          onClick={() => rateFAQ(faq.question, true)}
                        >
                          Yes
                        </button>
                        <button 
                          className={`px-4 py-1 text-sm rounded-md border ${
                            helpfulRatings[faq.question] === false 
                              ? 'bg-primary text-text-light border-primary' 
                              : 'bg-white text-text border-border hover:border-primary'
                          }`}
                          onClick={() => rateFAQ(faq.question, false)}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-lg text-text mb-4">Don't see your question here?</p>
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Contact Us
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQPage; 